import React, { useState, useEffect, useRef } from 'react';
import { MdAccountTree, MdExpandMore, MdExpandLess } from 'react-icons/md';

const SpecializationForm = ({ erdState, currentSpecialization, setCurrentSpecialization, editingMode, setEditingMode, onSaveEvent }) => {
  const [expandedSubEntities, setExpandedSubEntities] = useState({});
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);
  const inputTextRef = useRef(erdState.inputText);

  // Toggle individual sub-entity expansion
  const toggleSubEntityExpansion = (index) => {
    setExpandedSubEntities(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Add sub-entity
  const addSubEntity = () => {
    setCurrentSpecialization(prev => ({
      ...prev,
      subEntities: ['', ...prev.subEntities]
    }));

    // Shift all existing expansion indices up by 1
    setExpandedSubEntities(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        const numKey = parseInt(key);
        newState[numKey + 1] = prev[numKey];
      });
      // Auto-expand new sub-entity at index 0
      newState[0] = true;
      return newState;
    });
  };

  // Remove sub-entity
  const removeSubEntity = (index) => {
    setCurrentSpecialization(prev => ({
      ...prev,
      subEntities: prev.subEntities.filter((_, i) => i !== index)
    }));

    // Clean up expansion state
    setExpandedSubEntities(prev => {
      const newState = { ...prev };
      delete newState[index];
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newState[numKey - 1] = newState[numKey];
          delete newState[numKey];
        }
      });
      return newState;
    });
  };

  // Update sub-entity
  const updateSubEntity = (index, value) => {
    setCurrentSpecialization(prev => ({
      ...prev,
      subEntities: prev.subEntities.map((entity, i) =>
        i === index ? value : entity
      )
    }));
  };

  // Generate or update specialization notation
  const generateSpecializationNotation = () => {
    if (!currentSpecialization.superEntity.trim()) {
      alert('Please enter a super entity name');
      return;
    }

    const validSubEntities = currentSpecialization.subEntities.filter(entity => entity.trim() !== '');
    if (validSubEntities.length === 0) {
      alert('Please enter at least one sub-entity');
      return;
    }

    const specializationType = currentSpecialization.isDisjoint ? ' @disjoint' : ' @overlapping';
    const totalFlag = currentSpecialization.isTotal ? ' @total' : '';
    let specializationNotation = `[Specialization]\n${currentSpecialization.superEntity}${specializationType}${totalFlag}\n--`;

    specializationNotation += '\n';
    validSubEntities.forEach(subEntity => {
      specializationNotation += `${subEntity}\n`;
    });

    // Check if we're editing an existing component
    if (editingMode && editingMode.isEditing && editingMode.componentType === 'specialization' && editingMode.originalComponent) {
      // Use the exact original text block for replacement so that two specializations
      // with the same super entity are not confused with each other.
      const originalText = editingMode.originalComponent.originalText;
      let replaced = false;

      if (originalText && erdState.inputText.includes(originalText)) {
        // String.replace replaces only the first occurrence — correct behaviour.
        erdState.setInputText(erdState.inputText.replace(originalText, specializationNotation + '\n\n'));
        replaced = true;
      } else {
        // Fallback: match by name, first occurrence only
        const originalName = editingMode.originalComponent.name;
        const lines = erdState.inputText.split('\n');
        const newLines = [];
        let i = 0;
        while (i < lines.length) {
          const line = lines[i];
          if (!replaced && line.trim() === '[Specialization]' && i + 1 < lines.length) {
            const nextLine = lines[i + 1].trim();
            const nameMatch = nextLine.match(/^(\w+)/);
            if (nameMatch && nameMatch[1] === originalName) {
              newLines.push(...specializationNotation.split('\n'));
              replaced = true;
              i += 2;
              while (i < lines.length && !lines[i].trim().startsWith('[')) {
                i++;
              }
              continue;
            }
          }
          newLines.push(line);
          i++;
        }
        if (replaced) {
          erdState.setInputText(newLines.join('\n'));
        } else {
          erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + specializationNotation);
        }
      }

      setPendingSaveEvent('specialization_form_submitted');

      // Clear editing mode
      setEditingMode({
        isEditing: false,
        componentType: null,
        originalComponent: null
      });
    } else {
      console.log('Adding new specialization (not editing)');
      // Add new component
      erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + specializationNotation);
      // Set pending save event for after state update
      setPendingSaveEvent('specialization_added');
    }

    // Reset form after successful addition/edit
    setCurrentSpecialization({
      superEntity: '',
      isDisjoint: true,
      isTotal: false,
      subEntities: ['']
    });
  };

  // Effect to track inputText changes and trigger pending save events
  useEffect(() => {
    // Only trigger save if inputText actually changed and we have a pending event
    if (erdState.inputText !== inputTextRef.current && pendingSaveEvent && onSaveEvent) {
      inputTextRef.current = erdState.inputText;
      // Use setTimeout to ensure the state has fully propagated
      setTimeout(() => {
        onSaveEvent(pendingSaveEvent);
        setPendingSaveEvent(null);
      }, 0);
    } else {
      inputTextRef.current = erdState.inputText;
    }
  }, [erdState.inputText, pendingSaveEvent, onSaveEvent]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          {editingMode && editingMode.isEditing && editingMode.componentType === 'specialization'
            ? `Edit Specialization: ${editingMode.originalComponent?.name || 'Unknown'}`
            : 'Add Specialization'}
        </h2>
        <MdAccountTree className="w-6 h-6 text-gray-600" />
      </div>

      {/* Super Entity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Super Entity</label>
        <select
          value={currentSpecialization.superEntity}
          onChange={(e) => setCurrentSpecialization(prev => ({ ...prev, superEntity: e.target.value }))}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select super entity...</option>
          {erdState.availableEntities?.map((entity, idx) => (
            <option key={idx} value={entity}>{entity}</option>
          ))}
        </select>
      </div>

      {/* Specialization Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Specialization Type</label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="specializationType"
              checked={currentSpecialization.isDisjoint}
              onChange={() => setCurrentSpecialization(prev => ({ ...prev, isDisjoint: true }))}
              className="mr-2"
            />
            <span className="text-sm">Disjoint (each entity can belong to at most one subclass)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="specializationType"
              checked={!currentSpecialization.isDisjoint}
              onChange={() => setCurrentSpecialization(prev => ({ ...prev, isDisjoint: false }))}
              className="mr-2"
            />
            <span className="text-sm">Overlapping (entities can belong to multiple subclasses)</span>
          </label>
        </div>
      </div>

      {/* Total Specialization */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={currentSpecialization.isTotal || false}
            onChange={(e) => setCurrentSpecialization(prev => ({ ...prev, isTotal: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Total Specialization</span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">Every instance of the super entity must belong to at least one subclass. Shown as a double line from the super entity to the D/O node.</p>
      </div>

      {/* Sub-entities */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Sub-entities ({currentSpecialization.subEntities.filter(entity => entity.trim() !== '').length})
          </label>
          <button
            onClick={addSubEntity}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            + Add Sub-entity
          </button>
        </div>

        <div className="space-y-4">
          {currentSpecialization.subEntities.map((subEntity, index) => {
            const isExpanded = expandedSubEntities[index] !== false;

            return (
              <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden transition-all duration-200 mb-1">
                {/* Sub-entity Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => toggleSubEntityExpansion(index)}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-700">
                      {subEntity.trim() !== '' ? subEntity : `Sub-entity ${index + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentSpecialization.subEntities.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSubEntity(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 transition-colors duration-150"
                      >
                        Remove
                      </button>
                    )}
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-500" />
                      ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Sub-entity Content */}
                <div className={`transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    <select
                      value={subEntity}
                      onChange={(e) => updateSubEntity(index, e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    >
                      <option value="">Select sub-entity...</option>
                      {erdState.availableEntities?.map((entity, idx) => (
                        <option key={idx} value={entity}>{entity}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit Specialization Button */}
      <button
        onClick={generateSpecializationNotation}
        className="w-full bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-md font-medium transition-colors duration-200"
      >
        {editingMode && editingMode.isEditing && editingMode.componentType === 'specialization'
          ? 'Confirm Edits'
          : 'Add Specialization to Notation'}
      </button>
    </div>
  );
};

export default SpecializationForm;